"use client"

import { useRouter } from "next/navigation"
import { useState } from "react"
import { CommandRequestError, type CommandResponse, submitCommand } from "../lib/api-client.ts"
import type { RouteAction } from "../lib/route-actions.ts"
import { safeReceiptRoute } from "../lib/safe-route.ts"
import { AccountDeletionConditionsModal } from "./account-deletion-conditions-modal.tsx"
import { ActionFeedbackSheet } from "./action-feedback-sheet.tsx"
import { Icon } from "./icons.tsx"
import { PhraseText } from "./phrase-text.tsx"

export type AcknowledgementKind = "consent" | "deletion" | "financial" | null

const acknowledgementCopy = (kind: Exclude<AcknowledgementKind, null>): string => {
  if (kind === "deletion") return "잔액과 진행 중 거래를 확인했고 계정 삭제 결과를 이해했습니다."
  if (kind === "consent") return "필수 항목을 확인하고 동의합니다."
  return "금액과 취소 조건을 확인했습니다."
}

export const ActionForm = ({ acknowledgementKind, action, statusAction = false }: { readonly acknowledgementKind: AcknowledgementKind; readonly action: RouteAction; readonly statusAction?: boolean }) => {
  const router = useRouter()
  const [acknowledged, setAcknowledged] = useState(false)
  const [showDeletionConditions, setShowDeletionConditions] = useState(false)
  const [message, setMessage] = useState("")
  const [pending, setPending] = useState(false)
  const requiresAcknowledgement = acknowledgementKind !== null
  const actionClassName = statusAction ? "button primary state-action" : acknowledgementKind === "deletion" ? "button destructive" : "button primary"

  const submit = async (): Promise<void> => {
    if (pending) return
    if (requiresAcknowledgement && !acknowledged) {
      setMessage(acknowledgementKind === "deletion" ? "계정 삭제 전 확인 항목에 체크해 주세요." : "계속하려면 확인 항목에 체크해 주세요.")
      return
    }
    setPending(true)
    setMessage("")
    let result: CommandResponse | null = null
    try {
      result = await submitCommand(action.endpoint, action.body, action.idempotencyKey)
    } catch (error: unknown) {
      if (error instanceof CommandRequestError && error.details.code === "ACCOUNT_DELETION_BLOCKED") {
        const balance = (error.details.balanceWon ?? 0).toLocaleString("ko-KR")
        const pendingTransactions = error.details.pendingTransactionCount ?? 0
        setMessage(`계정을 삭제하려면 잔액과 처리 중인 거래가 없어야 합니다. 현재 잔액 ${balance}원, 처리 중 ${pendingTransactions}건을 먼저 확인해 주세요.`)
        return
      }
      if (error instanceof Error) setMessage("요청 결과를 확인하지 못했습니다. 상태 화면에서 안전하게 다시 확인해 주세요.")
      else throw error
    } finally {
      setPending(false)
    }
    if (result === null) return
    const nextRoute = safeReceiptRoute(result, action)
    if (nextRoute === null) {
      setMessage("요청 결과를 안전하게 열 수 없습니다. 홈에서 상태를 다시 확인해 주세요.")
      return
    }
    router.push(nextRoute)
  }

  return (
    <div className="action-area">
      {requiresAcknowledgement ? (
        <>
          <label className="check-row">
            <input checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} type="checkbox" />
            <span>
              <PhraseText text={acknowledgementCopy(acknowledgementKind)} />
            </span>
          </label>
          {acknowledgementKind === "deletion" ? (
            <>
              <button className="checkout-terms-trigger" onClick={() => setShowDeletionConditions(true)} type="button">
                계정 삭제 조건 보기
              </button>
              <AccountDeletionConditionsModal onClose={() => setShowDeletionConditions(false)} open={showDeletionConditions} />
            </>
          ) : null}
        </>
      ) : null}
      <button aria-busy={pending} aria-disabled={(requiresAcknowledgement && !acknowledged) || pending} className={actionClassName} data-state-action={statusAction ? "command" : undefined} disabled={pending} onClick={() => void submit()} type="button">
        {statusAction ? <Icon name="status" /> : null}
        <span>{pending ? `${action.label} 처리 중` : action.label}</span>
      </button>
      <ActionFeedbackSheet message={message} onClose={() => setMessage("")} title={acknowledgementKind === "deletion" ? "계정 삭제 전 확인해 주세요" : "확인이 필요해요"} />
    </div>
  )
}
